import { AxiosResponse, HttpStatusCode } from 'axios';
import { Select } from 'chakra-react-select';
import React, { useState, useEffect } from 'react';
import {
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../../../context/AlertProvider';
import { useApi } from '../../../../../../context/ApiProvider';
import CustomChakraButton from '../../../../../CustomChakraButton/CustomChakraButton';
import CustomInput from '../../../../../CustomInput/CustomInput';
import { WaitingScreenForAdmin } from '../../../../Reports/SingleReport/WaitingScreenForAdmin';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
}

const PrintMenu = ({ isOpen, handleClose }: Props) => {
  const today = new Date();
  const { setShow: setShowAlert, setAlertProperties } = useAlertContext();
  const [showWaitingScreen, setShowWaitingScreen] = useState(false);
  const {
    apiReportController
  } = useApi();
  const { id: tenantId } = useParams<{ id: string }>();

  const [dateScope, setDateScope] = useState<{
      start: string | undefined;
      end: string | undefined;
   }>({
      start: today.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
   });

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newStartDate = event.target.value;
      if (dateScope.start !== newStartDate) {
        setDateScope((prev) => ({ ...prev, start: newStartDate, end: newStartDate }));
      }
    };

    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newEndDate = event.target.value;
      if (dateScope.start && newEndDate < dateScope.start) {
        return;
      }
      if (dateScope.end !== newEndDate) {
        setDateScope((prev) => ({ ...prev, end: newEndDate }));
      }
    };

  const handlePrintMenu = async (outputType: 'PDF' | 'XLSX') => {
    setShowWaitingScreen(true);
    try {
      const response = await apiReportController('menu-for-period').get('', {
        params: {
          fromDate: `${dateScope.start}`,
          toDate: `${dateScope.end}`,
          tenantId: tenantId,
          outputType
        },
        responseType: 'blob'
      });

      const fileName = response.headers['content-disposition']?.split('filename=')[1];

      const blob = new Blob([response.data], {
        type: 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setShowWaitingScreen(false);
    }
    handleClose();
  };

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
        <ModalOverlay
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
        <ModalContent>
          <ModalHeader className="flex flex-col">
            <h2 className="font-semibold text-lg">Pobierz jadłospis na wybrany okres</h2>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="grid gap-spacing-3xl">
              <div className="flex items-center justify-between">
                  <span className="mr-2 font-semibold">Wybierz okres:</span>
                  <div className="w-36">
                    <Input
                      required
                      color="gray"
                      type="date"
                      value={dateScope.start}
                      onChange={handleStartDateChange}
                    />
                  </div>
                  <p className="text-grayLight-700">-</p>
                  <div className="w-36">
                    <Input
                      required
                      color="gray"
                      type="date"
                      value={dateScope.end}
                      onChange={handleEndDateChange}
                    />
                  </div>
              </div>
          </ModalBody>
          <Divider className="pt-spacing-2xl pb-spacing-2xl" />
          <ModalFooter className="w-full flex justify-between gap-spacing-lg">
            <CustomChakraButton
              hierarchy="secondaryColor"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                className: 'grow',
                onClick: handleClose
              }}>
              Zamknij
            </CustomChakraButton>
            <CustomChakraButton
              hierarchy="primary"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => handlePrintMenu('XLSX'),
                className: 'grow'
              }}>
              Pobierz XLSX
            </CustomChakraButton>
            <CustomChakraButton
              hierarchy="primary"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => handlePrintMenu('PDF'),
                className: 'grow'
              }}>
              Pobierz PDF
            </CustomChakraButton>
          </ModalFooter>
        </ModalContent>
      <WaitingScreenForAdmin
         isOpen={showWaitingScreen}
         onClose={() => {}}
       />
    </Modal>
  );
};

export default PrintMenu;
